import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { account } from "../lib/appwrite.js";
import {
  clearAppwriteSession,
  stashFromPasswordReset,
} from "../lib/authSession.js";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userId = searchParams.get("userId");
  const secret = searchParams.get("secret");
  const resetComplete =
    searchParams.get("success") === "1";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState(null); // "loading" | "success" | "error"
  const [error, setError] = useState("");

  const showSuccess = resetComplete || status === "success";
  const missingToken = !showSuccess && (!userId || !secret);

  async function goToSignIn() {
    await clearAppwriteSession();
    stashFromPasswordReset();
    window.location.assign("/login");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords don't match.");
      setStatus("error");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      setStatus("error");
      return;
    }
    setStatus("loading");
    setError("");
    try {
      await account.updateRecovery(userId, secret, password);
      await clearAppwriteSession();
      setStatus("success");
      navigate("/reset-password?success=1", { replace: true });
    } catch (err) {
      setError(err?.message ?? "Something went wrong. Please try again.");
      setStatus("error");
    }
  }

  return (
    <div className="hc-page-shell relative min-h-screen w-full overflow-hidden font-sans">
      <div
        className="pointer-events-none absolute left-1/2 top-[40%] size-[320px] -translate-x-[120%] rounded-full bg-hc-warm/15 blur-[64px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-[42%] size-[288px] translate-x-[20%] rounded-full bg-hc-secondary/20 blur-[64px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[384px] flex-col items-center justify-center px-4 py-16">
        <img src="/logo_full.png" alt="Hey Coach AI logo" className="w-full max-w-[384px] h-auto" />

        <div className="w-full max-w-[384px] rounded-[16px] border border-hc-surface-border-strong bg-hc-surface p-7 shadow-[0px_25px_50px_0px_rgba(0,0,0,0.35)]">
          {missingToken ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-hc-error/15">
                <i className="icon-x text-xl text-hc-error leading-none" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">Invalid reset link</h1>
                <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                  This link is missing required information. Please request a new password reset.
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="mt-2 text-xs text-hc-primary hover:underline"
              >
                Back to sign in
              </button>
            </div>
          ) : showSuccess ? (
            <div className="flex flex-col items-center gap-4 py-2 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-hc-primary/15">
                <i className="icon-check text-xl text-hc-primary leading-none" aria-hidden="true" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">Password updated</h1>
                <p className="mt-2 text-xs leading-5 text-hc-text-dim">
                  Your password has been reset successfully. You can now sign in with your new password.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void goToSignIn()}
                className="relative mt-2 flex h-[48px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-sm font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110"
              >
                Sign in
                <i className="icon-arrow-right" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-lg font-bold tracking-[-0.44px] text-hc-text">Choose a new password</h1>
              <p className="mt-2 text-xs leading-4 text-hc-text-dim">
                Pick a strong password you don't use elsewhere.
              </p>

              <form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="new-password" className="text-xs font-medium text-hc-text-muted">
                    New password
                  </label>
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] py-3 pr-12 pl-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-hc-text-dim"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      <i className={`${showPassword ? "icon-eye-off" : "icon-eye"} block leading-none`} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="confirm-password" className="text-xs font-medium text-hc-text-muted">
                    Confirm new password
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                      required
                      placeholder="••••••••"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="h-[46px] w-full rounded-[14px] border border-hc-surface-border-strong bg-[rgba(255,255,255,0.06)] py-3 pr-12 pl-4 text-sm tracking-[-0.15px] text-hc-text placeholder:text-hc-text-dim outline-none ring-hc-primary/35 focus:ring-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute top-1/2 right-3 size-4 -translate-y-1/2 text-hc-text-dim"
                      aria-label={showConfirm ? "Hide password" : "Show password"}
                    >
                      <i className={`${showConfirm ? "icon-eye-off" : "icon-eye"} block leading-none`} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {status === "error" && (
                  <p className="rounded-[10px] border border-hc-error/30 bg-hc-error/10 px-3 py-2 text-xs text-hc-error">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="relative mt-2 flex h-[52px] w-full items-center justify-center gap-2 rounded-[14px] px-4 text-base font-semibold tracking-[-0.31px] text-white transition hc-brand-gradient hc-shadow-brand hover:brightness-110 disabled:opacity-60"
                >
                  {status === "loading" ? "Updating…" : "Set new password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
